import { Chip, Stack, Table, TableHead, TableRow, Typography } from '@mui/material';
import type { ShareInvoicesRequest } from '@vy/api-definition';
import { getSelectorAttribute, invoicesOverviewPageSelectors } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import type { Currency } from '@vy/types';
import { extract, isMultiple, isNotNullOrUndefined, isSingleItem, type ItemOf } from '@vy/utils';
import React from 'react';
import { Price } from '../../../../components/design/Price';
import { VyDialog } from '../../../../components/new-design/feedback/VyDialog';
import { useShareInvoices } from '../../../../hooks/requests/use-share-invoices';
import { useDialog } from '../../../../hooks/use-dialog';
import { runMutation } from '../../../../utils/vy-mutation';
import { WARNING_MULTIPLE, WARNING_SINGLE } from './share-invoices-dialog-const';
import { ShareInvoicesTableHeaderCell } from './ShareInvoicesTableHeaderCell';
import { ShareInvoicesTableCell } from './ShareInvoiceTableCell';

export type ShareInvoiceData = {
  invoiceId: string;
  invoiceNumber: string;
  customerName: string;
  contactEmails: string[];
  issueDate?: Date;
  amount: number;
  currency: Currency;
  isStripeSyncMissing: boolean;
};

type ShareInvoicesDialogProps = {
  invoicesData: ShareInvoiceData[];
  children: React.ReactElement;
};

export const ShareInvoicesDialog: React.FC<ShareInvoicesDialogProps> = ({ invoicesData, children }) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const shareInvoiceMutation = useShareInvoices();
  const invoicesIds = extract(invoicesData, 'invoiceId');

  const handleShare = async () => {
    handleClose();

    const payload = invoicesData.map<ItemOf<ShareInvoicesRequest>>(({ invoiceId, contactEmails }) => ({
      invoiceId,
      emailOverride: isSingleItem(contactEmails) ? contactEmails.at(0) : undefined,
    }));

    await runMutation(shareInvoiceMutation, payload, {
      error: `Failed to send Email${isMultiple(invoicesIds) ? 's' : ''}`,
    });
  };

  const shouldShowWarning = invoicesData.some(({ isStripeSyncMissing }) => isStripeSyncMissing);
  const warning = isSingleItem(invoicesData) ? WARNING_SINGLE : WARNING_MULTIPLE;

  return (
    <>
      {React.cloneElement(children, { onClick: handleOpen })}
      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        cancelButtonText="Cancel"
        warningText={shouldShowWarning ? warning : undefined}
        ctaText={
          <Stack
            direction="row"
            {...getSelectorAttribute(invoicesOverviewPageSelectors.shareInvoiceDialog.shareButton)}
            sx={{
              alignItems: "center",
              gap: 1
            }}>
            <VyIcon icon="Send" />
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "15px",
                lineHeight: "26px"
              }}>
              Share
            </Typography>
          </Stack>
        }
        onSubmit={() => handleShare()}
        label={`Share ${invoicesIds.length} invoice${isMultiple(invoicesIds) ? 's' : ''}`}
      >
        <Stack
          direction="column"
          sx={{
            width: "100%",
            gap: 2
          }}>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 1
            }}>
            <Typography variant="subtitle1" sx={{
              color: "text.primary"
            }}>
              Invoices
            </Typography>
            <Chip size="small" label={invoicesIds.length} />
          </Stack>
          <Table width="100%" size="small">
            <TableHead>
              <TableRow>
                <ShareInvoicesTableHeaderCell text="Invoice number" />
                <ShareInvoicesTableHeaderCell text="Customer" />
                <ShareInvoicesTableHeaderCell text="Emails" />
                <ShareInvoicesTableHeaderCell text="Issue Date" />
                <ShareInvoicesTableHeaderCell text="Amount" />
              </TableRow>
            </TableHead>
            {invoicesData.map(
              ({ invoiceId, invoiceNumber, customerName, contactEmails, issueDate, amount, currency }) => (
                <TableRow
                  key={invoiceId}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '& .MuiTableCell-sizeSmall': { paddingY: 2 },
                  }}
                >
                  <ShareInvoicesTableCell text={invoiceNumber} />
                  <ShareInvoicesTableCell text={customerName} />
                  <ShareInvoicesTableCell
                    text={contactEmails.map((email, index) => (
                      <span key={index}>
                        {email}
                        {index < contactEmails.length - 1 && <br />}
                      </span>
                    ))}
                  />
                  <ShareInvoicesTableCell
                    text={isNotNullOrUndefined(issueDate) ? issueDate.toLocaleDateString() : ''}
                  />
                  <ShareInvoicesTableCell
                    text={
                      <Price
                        {...getSelectorAttribute(
                          invoicesOverviewPageSelectors.shareInvoiceDialog.amountField,
                        )}
                        variant="body1"
                        sx={{ fontWeight: 400, fontSize: '16px', lineHeight: '24px' }}
                        color="text.primary"
                        currency={currency}
                      >
                        {amount}
                      </Price>
                    }
                  />
                </TableRow>
              ),
            )}
          </Table>
        </Stack>
      </VyDialog>
    </>
  );
};
